import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import { CompanyUsersStatus } from '@shared/types/companies.types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useAbandonCompanyMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useCompanyUsers } from '../../hooks/useQueries/useCompanies'
import { useSnackbar } from '../../hooks/useSnackbar'

const dialogStyles = {
  '& .MuiDialog-paper': {
    width: '32.5625rem',
    padding: '1rem'
  }
}

const dialogTitleStyles = {
  padding: '1rem 1.5rem'
}

const dialogContentStyles = {
  padding: '1.5rem',
  marginBottom: '2rem'
}

const dialogActionsStyles = {
  p: '.5rem'
}

export const AbandonCompanyModal = NiceModal.create(
  ({ companyId, companyName }: { companyId: string; companyName: string }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const { userId } = useAuthStore()
    const { data: companyUsers, isLoading: isCompanyUsersLoading } =
      useCompanyUsers(companyId)
    const { showSnackbar } = useSnackbar()

    const abandonCompanyMutation = useAbandonCompanyMutation()

    if (isCompanyUsersLoading) {
      return (
        <Dialog open>
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '3rem'
            }}
          >
            <CircularProgress />
          </DialogContent>
        </Dialog>
      )
    }

    const admins = companyUsers?.filter(
      companyUser =>
        companyUser.status === CompanyUsersStatus.ACTIVE &&
        companyUser.role.fullAccess
    )

    return (
      <>
        {admins && admins.length > 1 ? (
          <Dialog
            open={modal.visible}
            onClose={() => modal.remove()}
            maxWidth='sm'
            sx={dialogStyles}
          >
            <DialogTitle sx={dialogTitleStyles}>
              {t('abandon_company_modal.title_admin', { companyName })}
            </DialogTitle>
            <DialogContent sx={dialogContentStyles}>
              <DialogContentText>
                {t('abandon_company_modal.text_admin')}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={dialogActionsStyles}>
              <Button
                variant='outlined'
                color='secondary'
                onClick={() => modal.remove()}
                sx={{
                  textTransform: 'none',
                  padding: '.375rem 1rem',
                  color: 'secondary.light',
                  backgroundColor: 'custom.semiTransparentIndigo'
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='contained'
                color='error'
                sx={{ textTransform: 'none' }}
                onClick={() =>
                  abandonCompanyMutation.mutate(
                    { userId: userId!, companyId },
                    {
                      onSuccess: () => {
                        showSnackbar({
                          message: t('abandon_company_modal.success', {
                            companyName
                          }),
                          severity: 'success'
                        })
                      },
                      onError: () => {
                        showSnackbar({
                          message: t('abandon_company_modal.error', {
                            companyName
                          }),
                          severity: 'error'
                        })
                      },
                      onSettled: () => {
                        modal.remove()
                      }
                    }
                  )
                }
                disabled={abandonCompanyMutation.isPending}
              >
                {abandonCompanyMutation.isPending
                  ? t('abandon_company_modal.leaving')
                  : t('abandon_company_modal.leave')}
              </Button>
            </DialogActions>
          </Dialog>
        ) : (
          <Dialog
            open={modal.visible}
            onClose={() => modal.remove()}
            sx={dialogStyles}
          >
            <DialogTitle sx={dialogTitleStyles}>
              {t('abandon_company_modal.title')}
            </DialogTitle>
            <DialogContent sx={dialogContentStyles}>
              <DialogContentText>
                {t('abandon_company_modal.text')}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={dialogActionsStyles}>
              <Button
                variant='outlined'
                color='secondary'
                onClick={() => modal.remove()}
                sx={{
                  textTransform: 'none',
                  padding: '.375rem 1rem',
                  color: 'secondary.light',
                  backgroundColor: 'custom.semiTransparentIndigo'
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                component={Link}
                to={`/companies/${companyId}/members`}
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  modal.remove()
                }}
              >
                {t('abandon_company_modal.assign_admin')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  }
)
